import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Filter,
  FilterCheckTracker,
} from "@companion-core/shared/app/Components/Inputs/JPInputFilter/JPInputFilter.model";
import { useFiltersStore } from "@companion-core/shared/app/Store/filters";
import { formatFilters } from "@companion-core/shared/app/Utils/filters";
import { trackClickResetFilters } from "@companion-core/shared/app/tracking/filters";

interface useJPInputFilterProps {
  filters: Filter[];
  onValueChange?: (filters: Filter[]) => void;
}

export const useJPInputFilter = ({ filters, onValueChange }: useJPInputFilterProps) => {
  const { t, i18n } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<Filter[] | null>(null);
  const [checkedOptions, setCheckedOptions] = useState<FilterCheckTracker[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [autocompleteFilterLabel, setAutocompleteFilterLabel] = useState<string | undefined>();

  const optionsTotalCount = useMemo(() => {
    let count = 0;
    filters?.map((filter) =>
      filter.options.map((option) => {
        count += option.count;
      })
    );
    return count;
  }, [filters]);

  const checkedOptionsCount = useMemo(() => {
    let count = 0;
    filterOptions?.map((filter) =>
      filter.options.map((option) => {
        if (option.checked) count += 1;
      })
    );
    return count;
  }, [filterOptions]);

  const resultCount = useMemo(() => {
    let count = 0;
    if (checkedOptionsCount === 0) return optionsTotalCount;
    filterOptions?.map((filter) =>
      filter.options.map((option) => {
        if (option.checked) count += option.count;
      })
    );
    return count;
  }, [filterOptions, checkedOptionsCount]);

  const setInitialValues = useCallback(() => {
    setFilterOptions(filters);
    const checkedOptionsValues: FilterCheckTracker[] = [];
    filters.map((filter) => {
      const filterTracker: FilterCheckTracker = {
        name: filter.name,
        checked: [],
      };
      filter.options.map((option) => {
        if (option.checked) filterTracker.checked.push(option.key);
      });
      checkedOptionsValues.push(filterTracker);
    });
    setCheckedOptions(checkedOptionsValues);
  }, [filters]);

  const updateCheckedValues = useCallback(
    (name: string, key: string, checked: boolean) => {
      const checkedOptionsToUpdate = [...checkedOptions];
      const indexToUpdate = checkedOptionsToUpdate.findIndex((filter) => filter.name === name);
      if (checked) {
        checkedOptionsToUpdate[indexToUpdate].checked.push(key);
      } else {
        const indexOfValue = checkedOptionsToUpdate[indexToUpdate].checked.indexOf(key);
        checkedOptionsToUpdate[indexToUpdate].checked.splice(indexOfValue, 1);
      }
      setCheckedOptions(checkedOptionsToUpdate);
    },
    [checkedOptions]
  );

  const hasCheckedOptions = useCallback(
    (name?: string, hasGeographicFilters = false) => {
      if (name) {
        return !!filterOptions
          ?.find((value) => value.name === name)
          ?.options.find((value) => value.checked === true);
      }
      if (hasGeographicFilters) return true;
      let hasChecked = false;
      filterOptions?.map((filter) => {
        const didFind = filter.options.find((value) => value.checked === true);
        if (didFind) hasChecked = !!didFind;
      });
      return hasChecked;
    },
    [filterOptions]
  );

  const resetFilter = useCallback(
    (name: string) => {
      if (!filterOptions) return;
      const indexToUpdate = filterOptions.findIndex((filter) => filter.name === name);
      if (indexToUpdate === -1) return;

      const filtersToUpdate = filterOptions.map((filter, idx) =>
        idx !== indexToUpdate
          ? filter
          : {
              ...filter,
              options: filter.options.map((option) => ({ ...option, checked: false })),
            }
      );
      setFilterOptions(filtersToUpdate);
      if (onValueChange) onValueChange(filtersToUpdate);

      const checkedIndexToUpdate = checkedOptions.findIndex((filter) => filter.name === name);
      if (checkedIndexToUpdate === -1) return;

      const checkedOptionsToUpdate = checkedOptions.map((item, idx) =>
        idx !== checkedIndexToUpdate ? item : { ...item, checked: [] as string[] }
      );
      setCheckedOptions(checkedOptionsToUpdate);
    },
    [filterOptions, checkedOptions, onValueChange]
  );

  const resetAllFilters = useCallback(() => {
    if (!filterOptions) return;
    const filtersToUpdate = [...filterOptions];
    filtersToUpdate.map((filter) => filter.options.map((option) => (option.checked = false)));
    setFilterOptions(filtersToUpdate);
    const checkedOptionsToUpdate = [...checkedOptions];
    checkedOptionsToUpdate.map((filter) => (filter.checked = []));
    setCheckedOptions(checkedOptionsToUpdate);

    setAutocompleteFilterLabel(undefined);

    const persisted = { ...(useFiltersStore.getState().filters ?? {}) };
    delete persisted.geo_place_id;
    delete persisted.geo_place_level;
    const formattedFilters = formatFilters(filtersToUpdate);
    useFiltersStore.getState().setFilters({ ...persisted, ...formattedFilters });

    if (onValueChange) onValueChange(filtersToUpdate);
    trackClickResetFilters();
  }, [filterOptions, checkedOptions, onValueChange]);

  return {
    t,
    i18n,
    filterOptions,
    checkedOptions,
    isPopoverOpen,
    checkedOptionsCount,
    resultCount,
    setInitialValues,
    updateCheckedValues,
    hasCheckedOptions,
    resetFilter,
    resetAllFilters,
    setFilterOptions,
    setIsPopoverOpen,
    setAutocompleteFilterLabel,
    autocompleteFilterLabel,
  };
};
